/* eslint-disable react/prop-types */
import Carousel, { Pagination } from "react-native-reanimated-carousel"
import { ActivityIndicator, useWindowDimensions, View } from "react-native"
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  runOnJS,
} from "react-native-reanimated"
import {
  carouselAData,
  type CarouselSlide,
  type MyLeadsConnectViewProps,
} from "@/components/properties/myleads/MyLeadsConnectData"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
} from "@gorhom/bottom-sheet"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import { useViewedCallback } from "@/analytics/react"
import {
  myLeadsOnboardingViewed,
  myLeadsSetupClicked,
} from "@/analytics/events/properties"
import { router } from "expo-router"
import { Body, HeroTitle } from "@/components/standard/BFText"
import { ConnectPopover } from "@/components/properties/myleads/MyLeadsConnectPopover"
import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { BFColors } from "@/constants/Colors"
import { useMyLeadsBoostFeature } from "@/hooks/useMyLeadsBoostFeature"
import { Vertical } from "@/components/standard/BFLayout"
import Spacer from "@/components/util/Spacer"
import type LottieView from "lottie-react-native"
import { type AnimationObject } from "lottie-react-native"
import { useLottieFrameManager } from "@/hooks/useLottieFrameManager"
import myleadsA1 from "@/assets/lottie/myleads-a-1.json"
import myleadsA2 from "@/assets/lottie/myleads-a-2.json"
import myleadsA3 from "@/assets/lottie/myleads-a-3.json"
import myleadsA4 from "@/assets/lottie/myleads-a-4.json"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { BFLottieView } from "@/components/standard/animation/BFLottieView"
import { useIsDesktop } from "@/hooks/useIsDesktop"

// Ensure these are AnimationObjects
const lotties: Record<string, AnimationObject> = {
  "myleads-a-1": myleadsA1,
  "myleads-a-2": myleadsA2,
  "myleads-a-3": myleadsA3,
  "myleads-a-4": myleadsA4,
}

export default function MyLeadsConnectCarouselAView({
  onDismiss,
}: MyLeadsConnectViewProps) {
  const { handleMyLeadsConnectionSuccess, screenHeightCropped } =
    useMyLeadsBoostFeature(onDismiss)
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const { isWeb } = useDeviceAttributes()
  const { isEmbeddedBrowser } = useBrowserInfo()
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const [firstPlayComplete, setFirstPlayComplete] = useState(false)
  const progressValue = useSharedValue(0)

  const isDesktop = useIsDesktop()
  const slideWidth = isDesktop ? 428 : screenWidth

  const slides = useMemo(() => carouselAData.slides, [])

  // Store references to each LottieView instance
  const lottieRefs = useRef<Array<LottieView | null>>([])

  // Carousel entrance animation (exclude web)
  const translateX = useSharedValue(isWeb ? 0 : screenWidth)
  const [animationComplete, setAnimationComplete] = useState(false)

  useEffect(() => {
    if (!isWeb) {
      translateX.value = withTiming(0, { duration: 400 }, (isFinished) => {
        if (isFinished) {
          runOnJS(setAnimationComplete)(true)
        }
      })
    }
  }, [translateX])

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [] // Track all timeouts

    if (animationComplete) {
      const firstSlideLottie = lottieRefs.current[0]
      if (firstSlideLottie) {
        const playTimeout = setTimeout(() => {
          firstSlideLottie.play(5, 83)

          const completeTimeout = setTimeout(() => {
            setFirstPlayComplete(true)
          }, 1000) // Delay to mark first play as complete

          timeouts.push(completeTimeout) // Store the second timeout
        }, 1000) // 1-second delay before playing the animation

        timeouts.push(playTimeout) // Store the first timeout
      }
    }

    // Cleanup on unmount
    return () => {
      timeouts.forEach((timeout) => {
        clearTimeout(timeout)
      })
    }
  }, [animationComplete])

  // Hook for managing Lottie animations
  const { onProgressChange } = useLottieFrameManager({
    totalSlides: slides.length,
    lottieRefs,
    slideWidth,
  })

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }))

  useViewedCallback(() => {
    myLeadsOnboardingViewed()
  }, [])

  const onStart = useCallback(() => {
    if (isEmbeddedBrowser) {
      router.push("/properties/myleads/redirect")
    }

    bottomSheetModalRef.current?.present()
    myLeadsSetupClicked()
  }, [])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  const slideHeight = screenHeight * 0.6
  const dotSize = 6
  const dotSpacing = 5

  const renderItem = useCallback(
    ({ item, index }: { item: CarouselSlide; index: number }) => {
      const animatedStyle = useAnimatedStyle(() => {
        const distance = Math.abs(index - progressValue.value)
        const clampedDistance = Math.min(distance, 1)
        const opacity = 1 - 1 * clampedDistance
        return { opacity }
      })

      return (
        <Animated.View style={[{ height: "100%" }, animatedStyle]}>
          <CarouselPageA
            ref={(ref) => (lottieRefs.current[index] = ref)}
            title={item.title}
            subtitle={item.subtitle}
            lottieFileKey={item.lottieFile}
            dynamicHeight={item.graphicHeight}
          />
        </Animated.View>
      )
    },
    [progressValue]
  )

  return (
    <Vertical className={screenHeightCropped ? "flex-1" : "flex-1 mt-8"}>
      <Animated.View
        style={[
          {
            position: "relative",
            minHeight: 450,
            width: screenWidth,
            alignSelf: "center",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          },
          animatedStyle,
        ]}
      >
        <Carousel
          data={slides}
          renderItem={renderItem}
          mode="parallax"
          modeConfig={{
            parallaxScrollingScale: 0.99,
            parallaxAdjacentItemScale: 0.88,
          }}
          width={slideWidth}
          height={slideHeight}
          loop={false}
          autoPlay={false}
          scrollAnimationDuration={1005}
          onProgressChange={(offsetProgress, absoluteProgress) => {
            if (firstPlayComplete || isWeb) {
              // Update lottie frames if needed
              onProgressChange(offsetProgress)
              // Use absoluteProgress to determine slide positions
              progressValue.value = absoluteProgress
            }
          }}
        />
        <Pagination.Basic
          progress={progressValue}
          data={slides}
          dotStyle={{
            width: dotSize,
            height: dotSize,
            borderRadius: 4,
            backgroundColor: BFColors.lightest,
            marginHorizontal: dotSpacing,
          }}
          activeDotStyle={{
            width: dotSize,
            height: dotSize,
            borderRadius: 4,
            backgroundColor: BFColors.teal,
          }}
          size={dotSize}
          containerStyle={{
            alignSelf: "center",
            marginTop: 8,
            marginBottom: isDesktop ? 40 : 12,
          }}
        />
      </Animated.View>
      {!isDesktop && <Spacer />}
      <PrimaryDarkButton
        testID={"my-leads-button"}
        title={carouselAData.ctaText}
        onPress={onStart}
        className="mb-8"
        style={{ alignSelf: "center", width: 330 }}
      />

      {!isEmbeddedBrowser && (
        <BottomSheetModal
          ref={bottomSheetModalRef}
          snapPoints={["70%"]}
          backdropComponent={renderBackdrop}
          index={0}
          backgroundStyle={{ backgroundColor: BFColors.calmBlue }}
          handleIndicatorStyle={{ backgroundColor: "#FFFFFF" }}
          enableDynamicSizing={false}
        >
          <ConnectPopover
            onSuccess={handleMyLeadsConnectionSuccess}
            onDismiss={() => {
              bottomSheetModalRef?.current?.dismiss()
            }}
          />
        </BottomSheetModal>
      )}
    </Vertical>
  )
}

interface CarouselPageAProps {
  title: string
  subtitle?: string
  lottieFileKey?: string
  dynamicHeight?: number
}

const CarouselPageA = forwardRef<LottieView, CarouselPageAProps>(
  ({ title, subtitle, lottieFileKey, dynamicHeight }, ref) => {
    const source =
      lottieFileKey && lotties[lottieFileKey]
        ? lotties[lottieFileKey]
        : undefined

    const { width: screenWidth } = useWindowDimensions()
    const { isWeb } = useDeviceAttributes()
    const isDesktop = useIsDesktop()

    return (
      <Vertical className="flex-1 flex-col p-4 max-h-[440px] relative self-center">
        <HeroTitle
          style={{
            position: "absolute",
            width: "100%",
            top: 10, // Adjust this value as needed for proper alignment
            alignItems: "center",
            alignSelf: "center",
          }}
          className="text-lightest"
        >
          {title}
        </HeroTitle>
        {subtitle && (
          <View
            style={{
              position: "absolute",
              top: 110,
              width: "100%",
              alignItems: "center",
              alignSelf: "center",
            }}
          >
            <Body className="text-beige text-center">{subtitle}</Body>
          </View>
        )}
        <View style={{ marginTop: 130 }}>
          {source ? (
            <BFLottieView
              ref={ref}
              source={source}
              style={{
                width: isDesktop ? "150%" : isWeb ? "100%" : screenWidth * 0.75,
                height: "100%",
                alignSelf: "center",
              }}
              speed={2.4}
              loop={false}
              autoPlay={false}
              renderMode={"HARDWARE"}
            />
          ) : (
            <ActivityIndicator size="large" color={BFColors.teal} />
          )}
        </View>
      </Vertical>
    )
  }
)
